import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { claimsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './ClaimWorkspace.css';

const audienceLabel: Record<string, string> = { Client: 'Client', Insurer: 'Insurer', Internal: 'Internal team' };
const updateClass = (kind: string) => `claim-workspace-update ${kind.toLowerCase().replace(/[^a-z]/g, '-')}`;

const ClaimWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const isStaff = permissions.canManageClaims;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState({ kind: 'InternalNote', message: '', status: 'Open', nextUpdateDueAt: '', chaseAudience: 'Internal', isClientVisible: false, sendEmail: false });
  const [documentCategory, setDocumentCategory] = useState('Client Correspondence');

  const load = async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const response = await claimsAPI.get(Number(id));
      setData(response.data);
      const claim = response.data.claim;
      setAction(current => ({ ...current, status: claim.status || 'Open', nextUpdateDueAt: claim.nextUpdateDueAt ? claim.nextUpdateDueAt.slice(0, 16) : '', chaseAudience: claim.nextChaseAudience || 'Internal' }));
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Unable to load this claim.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveUpdate = async () => {
    if (!id || !action.message.trim()) { setError('Enter an update before saving.'); return; }
    setSaving(true); setError(null);
    try {
      await claimsAPI.update(Number(id), { ...action, nextUpdateDueAt: action.nextUpdateDueAt || null });
      setAction(current => ({ ...current, message: '', sendEmail: false, isClientVisible: false }));
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Unable to save this claim update.');
    } finally { setSaving(false); }
  };

  const uploadDocument = async (file?: File) => {
    if (!id || !file) return;
    setError(null);
    try {
      await claimsAPI.uploadDocument(Number(id), file, documentCategory, false);
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Unable to upload this document.');
    }
  };

  const downloadDocument = async (document: any) => {
    if (!id) return;
    try {
      const response = await claimsAPI.downloadDocument(Number(id), document.id);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url; link.download = document.fileName; link.click();
      URL.revokeObjectURL(url);
    } catch { setError('Unable to download this document.'); }
  };

  if (loading) return <div>Loading claim workspace...</div>;
  if (!data) return <div className="claim-workspace"><button className="btn btn-secondary" onClick={() => navigate('/claims')}>Back to Claims Dashboard</button><p>{error || 'Claim not found.'}</p></div>;
  const claim = data.claim;

  return <div className="claim-workspace">
    <button className="claim-back" onClick={() => navigate('/claims')}>← Back to Claims Dashboard</button>
    {error && <div className="claim-workspace-error">{error}</div>}
    <header className="claim-workspace-header">
      <div><p className="claim-workspace-kicker">Motor claim workspace</p><h1>{claim.claimReference}</h1><p>{claim.clientNameSnapshot} · Submitted by {claim.submittedByName}</p></div>
      <span className={`claim-workspace-status ${claim.status.toLowerCase().replace(/\s+/g, '-')}`}>{claim.status}</span>
    </header>
    <section className="claim-workspace-summary">
      <div><small>Vehicle</small><strong>{claim.vehicleRegistrationSnapshot || 'Not recorded'}</strong><span>{claim.vehicleDescriptionSnapshot}</span></div>
      <div><small>Driver</small><strong>{claim.driverNameSnapshot || 'Not recorded'}</strong><span>Liability: {claim.liability || 'Undecided'}</span></div>
      <div><small>Next chase</small><strong>{claim.nextUpdateDueAt ? new Date(claim.nextUpdateDueAt).toLocaleString('en-GB') : 'Not scheduled'}</strong><span>{audienceLabel[claim.nextChaseAudience] || 'Internal team'}</span></div>
    </section>
    <div className={`claim-workspace-grid ${isStaff ? '' : 'read-only'}`}>
      <main className="claim-workspace-main">
        <section className="claim-workspace-panel">
          <div className="claim-panel-title"><div><p className="claim-workspace-kicker">Activity</p><h2>Claim timeline</h2></div></div>
          <div className="claim-workspace-timeline">
            {data.updates.map((item: any) => <article className={updateClass(item.kind)} key={item.id}>
              <div className="claim-update-meta"><strong>{item.kind.replace(/([A-Z])/g, ' $1')}</strong>{item.chaseAudience && <span>{audienceLabel[item.chaseAudience] || item.chaseAudience}</span>}</div>
              <p>{item.message}</p>
              <small>{item.createdByName} · {new Date(item.createdAt).toLocaleString('en-GB')}{item.nextUpdateDueAt ? ` · Next chase: ${new Date(item.nextUpdateDueAt).toLocaleString('en-GB')}` : ''}</small>
            </article>)}
          </div>
        </section>
        <section className="claim-workspace-panel">
          <div className="claim-panel-title"><div><p className="claim-workspace-kicker">Evidence</p><h2>Documents</h2></div><span>{data.documents.length} file{data.documents.length === 1 ? '' : 's'}</span></div>
          <ul className="claim-workspace-documents">{data.documents.map((document: any) => <li key={document.id}><button onClick={() => downloadDocument(document)}>{document.fileName}</button><span>{document.category} · {new Date(document.createdAt).toLocaleDateString('en-GB')}</span></li>)}</ul>
        </section>
      </main>
      {isStaff && <aside className="claim-workspace-action">
        <section className="claim-workspace-panel">
          <p className="claim-workspace-kicker">Claims Manager</p><h2>Record an update</h2>
          <label>Update type<select value={action.kind} onChange={event => setAction({ ...action, kind: event.target.value })}><option>InternalNote</option><option>Client Update</option><option>Request Information</option><option>Telephone Call</option><option>Insurer Correspondence</option><option>Settlement</option><option>Closure</option></select></label>
          <label>Update / communication<textarea value={action.message} onChange={event => setAction({ ...action, message: event.target.value })} placeholder="Record the action, response, or information requested." /></label>
          <label>Claim status<select value={action.status} onChange={event => setAction({ ...action, status: event.target.value })}><option>Open</option><option>Awaiting Information</option><option>With Insurer</option><option>Repair / Hire</option><option>Settled</option><option>Closed</option></select></label>
          <div className="claim-chase-grid"><label>Next chase due<input type="datetime-local" value={action.nextUpdateDueAt} onChange={event => setAction({ ...action, nextUpdateDueAt: event.target.value })} /></label><label>Chase audience<select value={action.chaseAudience} onChange={event => setAction({ ...action, chaseAudience: event.target.value })}><option value="Client">Client</option><option value="Insurer">Insurer</option><option value="Internal">Internal team</option></select></label></div>
          <label className="claim-check"><input type="checkbox" checked={action.isClientVisible} onChange={event => setAction({ ...action, isClientVisible: event.target.checked })} /> Visible in the client workspace</label>
          <label className="claim-check"><input type="checkbox" checked={action.sendEmail} onChange={event => setAction({ ...action, sendEmail: event.target.checked })} /> Send email to the selected chase audience</label>
          <button className="btn btn-primary claim-save-update" onClick={saveUpdate} disabled={saving}>{saving ? 'Saving...' : 'Save update & schedule chase'}</button>
        </section>
        <section className="claim-workspace-panel">
          <p className="claim-workspace-kicker">Correspondence</p><h2>Add document</h2>
          <label>Document category<select value={documentCategory} onChange={event => setDocumentCategory(event.target.value)}><option>Client Correspondence</option><option>Insurer Correspondence</option><option>Damage Evidence</option><option>Location Evidence</option><option>Other</option></select></label>
          <input className="claim-file-input" type="file" onChange={event => uploadDocument(event.target.files?.[0])} />
        </section>
      </aside>}
    </div>
  </div>;
};

export default ClaimWorkspace;
